import React, { Component } from 'react';
import AuthService from '../AuthService';
import { getRarityColor } from '../../utils/rarityBadge';
import constants from '../../config';

var Auth = new AuthService();

// Parse characteristics: split into tags vs "Name: X" vs "TRANSFORMATION: X"
function parseCharacteristics(charArray) {
  var tags = [];
  var charName = '';
  var charTransformation = '';
  (charArray || []).forEach(function(c) {
    var trimmed = c.trim();
    if (!trimmed) return;
    var nameLower = trimmed.toLowerCase();
    if (nameLower.indexOf('name:') === 0) {
      charName = trimmed.replace(/^name:\s*/i, '').trim();
    } else if (nameLower.indexOf('transformation:') === 0) {
      charTransformation = trimmed.replace(/^transformation:\s*/i, '').trim();
    } else if (nameLower === 'transform' || nameLower === 'transformation') {
      // skip bare "Transform"/"Transformation" — covered by the text input
    } else {
      tags.push(trimmed);
    }
  });
  return { tags: tags, charName: charName, charTransformation: charTransformation };
}

// Rebuild characteristics array from tags + name + transformation
function buildCharacteristics(tags, charName, charTransformation) {
  var result = (tags || []).slice();
  if (charName) result.push('Name: ' + charName);
  if (charTransformation) result.push('TRANSFORMATION: ' + charTransformation);
  return result;
}

// Clean up available characteristics from API — trim, dedup, remove Name:/TRANSFORMATION: prefixed
function cleanCharacteristicOptions(raw) {
  var seen = {};
  var result = [];
  (raw || []).forEach(function(c) {
    if (!c) return;
    var trimmed = c.trim();
    if (!trimmed) return;
    var lower = trimmed.toLowerCase();
    if (lower.indexOf('name:') === 0) return;
    if (lower.indexOf('transformation:') === 0) return;
    if (lower === 'transform' || lower === 'transformation') return;
    if (lower === 'camouflage' && seen['camouflage']) return;
    if (!seen[lower]) {
      seen[lower] = true;
      result.push(trimmed);
    }
  });
  return result.sort();
}

// Shared input class
var inputCls = 'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#111] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]';
var selectCls = 'tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#111] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]';
var labelCls = 'tw-text-xs tw-font-medium tw-text-[#888] dark:tw-text-[#999] tw-block tw-mb-1.5';

// Section header component — collapsible
function SectionHeader(props) {
  var color = props.color || '#D4A843';
  var isCollapsible = props.onToggle != null;
  var isOpen = props.open !== false;
  return (
    <div
      className={'tw-flex tw-items-center tw-gap-2.5 tw-mb-3 tw-mt-1' + (isCollapsible ? ' tw-cursor-pointer tw-select-none' : '')}
      onClick={isCollapsible ? props.onToggle : undefined}
    >
      <div className="tw-rounded-full" style={{ width: 8, height: 8, backgroundColor: color, flexShrink: 0 }} />
      <span className="tw-text-[11px] tw-uppercase tw-tracking-widest tw-font-bold" style={{ color: color }}>{props.children}</span>
      <div className="tw-flex-1 tw-h-px" style={{ backgroundColor: color, opacity: 0.2 }} />
      {isCollapsible && (
        <i className={'fa ' + (isOpen ? 'fa-chevron-up' : 'fa-chevron-down')} style={{ color: color, fontSize: 10 }} />
      )}
    </div>
  );
}

// Stat box for detail view
function StatBox(props) {
  return (
    <div className="tw-text-center tw-px-3 tw-py-2.5">
      <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-[#999] tw-mb-1">{props.label}</div>
      <div className="tw-text-sm tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">{props.value}</div>
    </div>
  );
}

// Detail row for clean label-value display
function DetailRow(props) {
  return (
    <div className="tw-flex tw-items-start tw-py-1.5" style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <span className="tw-text-xs tw-text-[#999] tw-w-28 tw-flex-shrink-0 tw-pt-0.5">{props.label}</span>
      <span className="tw-text-sm tw-text-[#333] dark:tw-text-[#ccc] tw-flex-1">{props.children}</span>
    </div>
  );
}

// Pill badge for elements/characteristics
function PillBadge(props) {
  var color = props.color || '#6B7280';
  return (
    <span
      className="tw-inline-flex tw-items-center tw-text-[11px] tw-font-medium tw-px-2.5 tw-py-1 tw-rounded-full"
      style={{ backgroundColor: color + '18', color: color, border: '1px solid ' + color + '30' }}
    >
      {props.icon && <i className={'fa ' + props.icon + ' tw-mr-1'} style={{ fontSize: 9 }} />}
      {props.children}
    </span>
  );
}

// Toggle switch component (module-level to avoid re-creation)
function ToggleSwitch(props) {
  var checked = props.checked;
  var onChange = props.onChange;
  var label = props.label;
  var color = props.color || '#D4A843';
  return (
    <label className="tw-flex tw-items-center tw-gap-2.5 tw-cursor-pointer tw-select-none">
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        onClick={function(e) { e.preventDefault(); onChange(!checked); }}
        className="tw-relative tw-rounded-full tw-transition-colors tw-border-0 tw-cursor-pointer"
        style={{ width: 40, height: 22, backgroundColor: checked ? color : '#ccc', padding: 0 }}
      >
        <div
          className="tw-absolute tw-rounded-full tw-bg-white tw-transition-all tw-shadow"
          style={{ width: 18, height: 18, top: 2, left: checked ? 20 : 2 }}
        />
      </button>
      <span className="tw-text-sm tw-text-[#666] dark:tw-text-[#aaa]">{label}</span>
    </label>
  );
}

// Checkbox group for multi-select array fields (module-level)
function CheckboxGroup(props) {
  var options = props.options || [];
  var selected = props.selected || [];
  var onToggle = props.onToggle;
  var newValue = props.newValue || '';
  var onNewValueChange = props.onNewValueChange;
  var onAddCustom = props.onAddCustom;

  return (
    <div>
      <div className="tw-flex tw-flex-wrap tw-gap-1.5">
        {options.map(function(opt) {
          var isSelected = selected.indexOf(opt) !== -1;
          return (
            <button
              key={opt}
              type="button"
              className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-border tw-cursor-pointer tw-transition-colors"
              style={{
                backgroundColor: isSelected ? '#D4A843' : 'transparent',
                borderColor: isSelected ? '#D4A843' : '#ddd',
                color: isSelected ? '#fff' : '#666',
              }}
              onClick={function() { onToggle(opt); }}
            >
              {opt}
            </button>
          );
        })}
        {/* Show selected values not in options */}
        {selected.filter(function(v) { return options.indexOf(v) === -1 && v; }).map(function(v) {
          return (
            <button
              key={v}
              type="button"
              className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-border tw-cursor-pointer"
              style={{ backgroundColor: '#D4A843', borderColor: '#D4A843', color: '#fff' }}
              onClick={function() { onToggle(v); }}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-1.5">
        <input
          type="text"
          className="tw-px-2 tw-py-1 tw-text-xs tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#111] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none"
          style={{ width: 120 }}
          placeholder="Add new..."
          value={newValue}
          onChange={function(e) { onNewValueChange(e.target.value); }}
          onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); onAddCustom(); } }}
        />
        {newValue && (
          <button
            type="button"
            className="tw-text-xs tw-px-2 tw-py-1 tw-rounded tw-border-0 tw-text-white tw-cursor-pointer"
            style={{ backgroundColor: '#10B981' }}
            onClick={onAddCustom}
          >
            <i className="fa fa-check" />
          </button>
        )}
      </div>
    </div>
  );
}

// Dropdown with "Add new..." option (module-level)
function DropdownWithCustom(props) {
  var value = props.value;
  var options = props.options || [];
  var onChange = props.onChange;
  var isCustom = props.isCustom;
  var onToggleCustom = props.onToggleCustom;
  var placeholder = props.placeholder || 'Select...';

  if (isCustom) {
    return (
      <div className="tw-flex tw-items-center tw-gap-1">
        <input
          type="text"
          className={inputCls}
          placeholder="Type new value..."
          value={value}
          onChange={function(e) { onChange(e.target.value); }}
        />
        <button
          type="button"
          className="tw-text-xs tw-px-2 tw-py-2 tw-rounded-lg tw-border tw-border-[#d0d0d0] tw-bg-transparent tw-text-[#999] tw-cursor-pointer tw-whitespace-nowrap"
          onClick={function() { onToggleCustom(false); }}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <select
      className={selectCls}
      value={value}
      onChange={function(e) {
        if (e.target.value === '__custom__') {
          onToggleCustom(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
    >
      <option value="">{placeholder}</option>
      {options.map(function(opt) { return <option key={opt} value={opt}>{opt}</option>; })}
      <option value="__custom__">+ Add new...</option>
    </select>
  );
}

class AdminCards extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cards: [],
      pagination: { page: 1, limit: 25, total: 0, pages: 0 },
      loading: true,
      search: '',
      searchTimer: null,
      filterGame: '',
      availableGames: [],
      filterSet: '',
      filterRarity: '',
      filterType: '',
      filterPlayable: '',
      filterErrata: '',
      filterArchived: '',
      sortBy: '',
      availableSets: [],
      availableRarities: [],
      availableTypes: [],
      availableElements: [],
      availableCharacteristics: [],
      availableAbilityCosts: [],
      // Expand / Edit
      expandedId: null,
      editingId: null,
      editLoading: false,
      editName: '',
      editCardNumber: '',
      editCardSet: '',
      editRarity: '',
      editCardType: '',
      editElement: [],
      editCharacteristic: [],
      editHandCost: '',
      editTurnCount: '',
      editEffectName: '',
      editSkill: '',
      editText: '',
      editHealthy: '',
      editInjured: '',
      editAbilityCost: [],
      editVariant: '',
      editArtist: '',
      editImage: '',
      editBackImage: '',
      editPlayable: true,
      editErrata: false,
      editCustomCardSet: false,
      editCustomCardType: false,
      editCharName: '',
      editCharTransformation: '',
      // Create mode
      createMode: false,
      createLoading: false,
      createName: '',
      createCardNumber: '',
      createCardSet: '',
      createRarity: '',
      createCardType: '',
      createElement: [],
      createCharacteristic: [],
      createHandCost: '',
      createTurnCount: '',
      createEffectName: '',
      createSkill: '',
      createText: '',
      createHealthy: '',
      createInjured: '',
      createAbilityCost: [],
      createVariant: '',
      createArtist: '',
      createImage: '',
      createBackImage: '',
      createPlayable: true,
      createErrata: false,
      createHorizontal: false,
      createRotation: 0,
      createGame: '',
      createCustomCardSet: false,
      createCustomCardType: false,
      createCharName: '',
      createCharTransformation: '',
      createImageFile: null,
      createImagePreview: null,
      createBackImageFile: null,
      createBackImagePreview: null,
      // Game for edit mode
      editGame: '',
      // Custom value inputs for checkbox groups
      newElementValue: '',
      newCharacteristicValue: '',
      newAbilityCostValue: '',
      // Image upload
      uploadingImage: false,
      uploadingBackImage: false,
      uploadError: null,
      // Sync
      syncing: false,
      syncResult: null,
      // Section collapse (create mode: only Identity open; edit mode: all open)
      sectionMechanics: false,
      sectionText: false,
      sectionMedia: false,
    };
    this.fetchCards = this.fetchCards.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleExport = this.handleExport.bind(this);
    this.handleSaveEdit = this.handleSaveEdit.bind(this);
    this.handleCreateCard = this.handleCreateCard.bind(this);
    this.handleUploadImage = this.handleUploadImage.bind(this);
    this.handleSyncCards = this.handleSyncCards.bind(this);
    this.toggleArrayValue = this.toggleArrayValue.bind(this);
    this.addCustomArrayValue = this.addCustomArrayValue.bind(this);
    this.handleClearOverride = this.handleClearOverride.bind(this);
    this.handleToggleArchive = this.handleToggleArchive.bind(this);
    this.imageInputRef = React.createRef();
    this.backImageInputRef = React.createRef();
    this.createImageInputRef = React.createRef();
    this.createBackImageInputRef = React.createRef();
  }

  componentDidMount() {
    this.fetchCards(1);
    this.fetchGames();
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
  }

  componentWillUnmount() {
    if (this.state.searchTimer) clearTimeout(this.state.searchTimer);
    if (this._handleThemeChanged) window.removeEventListener('themeChanged', this._handleThemeChanged);
  }

  fetchGames() {
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/games')
      .then(function(res) {
        self.setState({ availableGames: res.games || [] });
      })
      .catch(function(err) { console.log('Failed to fetch games:', err); });
  }

  getGameSets(gameId) {
    var game = this.state.availableGames.find(function(g) { return g._id === gameId; });
    if (!game || !game.setCategories) return [];
    var sets = [];
    game.setCategories.forEach(function(cat) {
      (cat.sets || []).forEach(function(s) {
        if (sets.indexOf(s) === -1) sets.push(s);
      });
    });
    return sets.sort();
  }

  getGameRarities(gameId) {
    var game = this.state.availableGames.find(function(g) { return g._id === gameId; });
    if (!game || !game.rarities) return [];
    return game.rarities.map(function(r) { return r.name; }).filter(Boolean);
  }

  fetchCards(page) {
    var self = this;
    self.setState({ loading: true });
    var params = new URLSearchParams();
    params.append('page', page || 1);
    params.append('limit', 25);
    if (self.state.search) params.append('search', self.state.search);
    if (self.state.filterGame) params.append('game', self.state.filterGame);
    if (self.state.filterSet) params.append('cardSet', self.state.filterSet);
    if (self.state.filterRarity) params.append('rarity', self.state.filterRarity);
    if (self.state.filterType) params.append('cardType', self.state.filterType);
    if (self.state.filterPlayable) params.append('playable', self.state.filterPlayable);
    if (self.state.filterErrata) params.append('errata', self.state.filterErrata);
    if (self.state.filterArchived) params.append('archived', self.state.filterArchived);
    if (self.state.sortBy) params.append('sort', self.state.sortBy);

    Auth.fetch(constants.DOMAIN + '/api/v1/admin/cards?' + params.toString())
      .then(function(data) {
        self.setState({
          cards: data.cards || [],
          pagination: data.pagination || {},
          loading: false,
          availableSets: (data.filters && data.filters.sets) || self.state.availableSets,
          availableRarities: (data.filters && data.filters.rarities) || self.state.availableRarities,
          availableTypes: (data.filters && data.filters.types) || self.state.availableTypes,
          availableElements: (data.filters && data.filters.elements) || self.state.availableElements,
          availableCharacteristics: data.filters && data.filters.characteristics ? cleanCharacteristicOptions(data.filters.characteristics) : self.state.availableCharacteristics,
          availableAbilityCosts: (data.filters && data.filters.abilityCosts) || self.state.availableAbilityCosts,
        });
      })
      .catch(function(err) {
        console.log('Failed to fetch cards:', err);
        self.setState({ loading: false });
      });
  }

  handleSearchChange(e) {
    var self = this;
    var value = e.target.value;
    if (self.state.searchTimer) clearTimeout(self.state.searchTimer);
    self.setState({
      search: value,
      searchTimer: setTimeout(function() { self.fetchCards(1); }, 400),
    });
  }

  handleExport() {
    var params = new URLSearchParams();
    if (this.state.search) params.append('search', this.state.search);
    if (this.state.filterSet) params.append('cardSet', this.state.filterSet);
    if (this.state.filterRarity) params.append('rarity', this.state.filterRarity);
    if (this.state.filterType) params.append('cardType', this.state.filterType);
    if (this.state.filterPlayable) params.append('playable', this.state.filterPlayable);
    if (this.state.filterErrata) params.append('errata', this.state.filterErrata);
    if (this.state.filterArchived) params.append('archived', this.state.filterArchived);

    Auth.fetch(constants.DOMAIN + '/api/v1/admin/cards/export?' + params.toString(), { rawResponse: true })
      .then(function(res) {
        return res.blob ? res.blob() : res.text();
      })
      .then(function(data) {
        var blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'cards-' + new Date().toISOString().split('T')[0] + '.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(function(err) {
        console.log('Failed to export cards:', err);
      });
  }

  toggleArrayValue(stateKey, value) {
    var arr = (this.state[stateKey] || []).slice();
    var idx = arr.indexOf(value);
    if (idx === -1) arr.push(value);
    else arr.splice(idx, 1);
    var update = {};
    update[stateKey] = arr;
    this.setState(update);
  }

  addCustomArrayValue(stateKey, inputKey) {
    var val = (this.state[inputKey] || '').trim();
    if (!val) return;
    var arr = (this.state[stateKey] || []).slice();
    if (arr.indexOf(val) === -1) arr.push(val);
    var update = {};
    update[stateKey] = arr;
    update[inputKey] = '';
    this.setState(update);
  }

  handleSaveEdit() {
    var self = this;
    self.setState({ editLoading: true });

    var body = {
      name: self.state.editName,
      cardNumber: self.state.editCardNumber,
      cardSet: self.state.editCardSet,
      rarity: self.state.editRarity,
      cardType: self.state.editCardType,
      element: self.state.editElement,
      characteristic: buildCharacteristics(self.state.editCharacteristic, self.state.editCharName, self.state.editCharTransformation),
      handCost: self.state.editHandCost,
      turnCount: self.state.editTurnCount ? (parseInt(self.state.editTurnCount) || null) : null,
      effectName: self.state.editEffectName,
      skill: self.state.editSkill,
      text: self.state.editText,
      healthy: self.state.editHealthy,
      injured: self.state.editInjured,
      abilityCost: self.state.editAbilityCost,
      variant: self.state.editVariant,
      artist: self.state.editArtist,
      image: self.state.editImage,
      backImage: self.state.editBackImage,
      playable: self.state.editPlayable,
      errata: self.state.editErrata,
      horizontal: self.state.editHorizontal,
      rotation: !isNaN(parseInt(self.state.editRotation)) ? parseInt(self.state.editRotation) : 0,
      game: self.state.editGame || null,
    };

    Auth.fetch(constants.DOMAIN + '/api/v1/admin/cards/' + self.state.editingId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function() {
        self.setState({ editingId: null, editLoading: false });
        self.fetchCards(self.state.pagination.page);
      })
      .catch(function(err) {
        console.log('Failed to update card:', err);
        self.setState({ editLoading: false });
        alert('Failed to update card: ' + (err.message || 'Unknown error'));
      });
  }

  handleCreateCard() {
    var self = this;
    if (!self.state.createName.trim()) { alert('Card name is required'); return; }
    if (!self.state.createRarity) { alert('Rarity is required'); return; }
    self.setState({ createLoading: true });

    var formData = new FormData();
    formData.append('name', self.state.createName);
    if (self.state.createCardNumber) formData.append('cardNumber', self.state.createCardNumber);
    if (self.state.createCardSet) formData.append('cardSet', self.state.createCardSet);
    formData.append('rarity', self.state.createRarity);
    if (self.state.createCardType) formData.append('cardType', self.state.createCardType);
    if (self.state.createElement.length) formData.append('element', JSON.stringify(self.state.createElement));
    var chars = buildCharacteristics(self.state.createCharacteristic, self.state.createCharName, self.state.createCharTransformation);
    if (chars.length) formData.append('characteristic', JSON.stringify(chars));
    if (self.state.createHandCost) formData.append('handCost', self.state.createHandCost);
    if (self.state.createTurnCount) formData.append('turnCount', parseInt(self.state.createTurnCount) || '');
    if (self.state.createEffectName) formData.append('effectName', self.state.createEffectName);
    if (self.state.createSkill) formData.append('skill', self.state.createSkill);
    if (self.state.createText) formData.append('text', self.state.createText);
    if (self.state.createHealthy) formData.append('healthy', self.state.createHealthy);
    if (self.state.createInjured) formData.append('injured', self.state.createInjured);
    if (self.state.createAbilityCost.length) formData.append('abilityCost', JSON.stringify(self.state.createAbilityCost));
    if (self.state.createVariant) formData.append('variant', self.state.createVariant);
    if (self.state.createArtist) formData.append('artist', self.state.createArtist);
    formData.append('playable', self.state.createPlayable);
    formData.append('errata', self.state.createErrata);
    formData.append('horizontal', self.state.createHorizontal);
    formData.append('rotation', !isNaN(parseInt(self.state.createRotation)) ? parseInt(self.state.createRotation) : 0);
    if (self.state.createGame) formData.append('game', self.state.createGame);
    if (self.state.createImageFile) formData.append('image', self.state.createImageFile);
    else if (self.state.createImage) formData.append('image', self.state.createImage);
    if (self.state.createBackImageFile) formData.append('backImage', self.state.createBackImageFile);
    else if (self.state.createBackImage) formData.append('backImage', self.state.createBackImage);

    fetch(constants.DOMAIN + '/api/v1/admin/cards', {
      method: 'POST',
      headers: { 'Authorization': Auth.getAuthHeader() },
      body: formData,
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(d) { throw new Error(d.error || 'Create failed'); });
        return res.json();
      })
      .then(function() {
        self.setState({
          createMode: false, createLoading: false,
          createName: '', createCardNumber: '', createCardSet: '', createRarity: '',
          createCardType: '', createElement: [], createCharacteristic: [],
          createHandCost: '', createTurnCount: '', createEffectName: '',
          createSkill: '', createText: '', createHealthy: '', createInjured: '',
          createAbilityCost: [], createVariant: '', createArtist: '',
          createImage: '', createBackImage: '', createPlayable: true, createErrata: false, createHorizontal: false, createRotation: 0,
          createGame: '', createCustomCardSet: false, createCustomCardType: false,
          createCharName: '', createCharTransformation: '',
          createImageFile: null, createImagePreview: null,
          createBackImageFile: null, createBackImagePreview: null,
        });
        self.fetchCards(1);
      })
      .catch(function(err) {
        console.log('Failed to create card:', err);
        self.setState({ createLoading: false });
        alert('Failed to create card: ' + (err.message || 'Unknown error'));
      });
  }

  handleUploadImage(cardId, field) {
    var self = this;
    var input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp';
    input.onchange = function(e) {
      var file = e.target.files[0];
      if (!file) return;
      var loadingKey = field === 'backImage' ? 'uploadingBackImage' : 'uploadingImage';
      var stateUpdate = {};
      stateUpdate[loadingKey] = true;
      self.setState(stateUpdate);

      var formData = new FormData();
      formData.append('image', file);

      fetch(constants.DOMAIN + '/api/v1/admin/cards/' + cardId + '/upload-image?field=' + field, {
        method: 'POST',
        headers: { 'Authorization': Auth.getAuthHeader() },
        body: formData,
      })
        .then(function(res) {
          if (!res.ok) return res.text().then(function(text) { try { var d = JSON.parse(text); throw new Error(d.error || 'Upload failed (HTTP ' + res.status + ')'); } catch(e) { if (e.message && e.message.indexOf('Upload failed') === 0) throw e; throw new Error('Upload failed (HTTP ' + res.status + ')'); } });
          return res.json();
        })
        .then(function(data) {
          var update = {};
          update[loadingKey] = false;
          if (data.card) {
            var editKey = field === 'backImage' ? 'editBackImage' : 'editImage';
            update[editKey] = data.card[field] || '';
          }
          self.setState(update);
          self.fetchCards(self.state.pagination.page);
        })
        .catch(function(err) {
          var resetUpdate = {};
          resetUpdate[loadingKey] = false;
          resetUpdate.uploadError = err.message || 'Failed to upload image';
          self.setState(resetUpdate);
        });
    };
    input.click();
  }

  handleSyncCards() {
    var self = this;
    if (!window.confirm('Sync cards from PrimalTCG Atlas?\n\nThis will import new cards and update existing ones. Sync-protected fields will NOT be overwritten.')) return;
    this.setState({ syncing: true, syncResult: null });
    fetch(constants.DOMAIN + '/api/v1/admin/sync-cards', {
      method: 'POST',
      headers: { 'Authorization': Auth.getAuthHeader() },
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        if (data.error) {
          self.setState({ syncing: false, syncResult: { error: data.error } });
        } else {
          self.setState({ syncing: false, syncResult: data });
          self.fetchCards(1);
        }
      })
      .catch(function(err) {
        console.log('Sync failed:', err);
        self.setState({ syncing: false, syncResult: { error: 'Sync request failed' } });
      });
  }

  handleClearOverride(cardId, field) {
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/cards/' + cardId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clearOverrides: [field] }),
    })
      .then(function() {
        self.fetchCards(self.state.pagination.page);
      })
      .catch(function(err) {
        console.log('Failed to clear override:', err);
      });
  }

  handleToggleArchive(cardId, currentArchived) {
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/admin/cards/' + cardId, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived: !currentArchived }),
    })
      .then(function() {
        self.fetchCards(self.state.pagination.page);
      })
      .catch(function(err) {
        console.log('Failed to toggle archive:', err);
      });
  }

  renderCardForm(prefix, card) {
    var self = this;
    var isEdit = prefix === 'edit';
    var p = prefix;

    var cardSetKey = p + 'CardSet';
    var rarityKey = p + 'Rarity';
    var cardTypeKey = p + 'CardType';
    var customCardSetKey = p + 'CustomCardSet';
    var customCardTypeKey = p + 'CustomCardType';

    // Check if selected game has a specific cardField key
    var selectedGame = self.state.availableGames.find(function(g) { return g._id === self.state[p + 'Game']; });
    var gameFields = selectedGame ? (selectedGame.cardFields || []) : [];
    var hasField = function(key) {
      // No game selected = show all fields (backwards-compat)
      if (!self.state[p + 'Game']) return true;
      return gameFields.some(function(f) { return f.key === key; });
    };
    var hasAnyMechanics = !self.state[p + 'Game'] || gameFields.length > 0;

    var imgSrc = self.state[p + 'Image'];
    if (imgSrc && imgSrc.indexOf('http') !== 0) imgSrc = constants.DOMAIN + '/' + imgSrc;

    return (
      <div className="tw-flex tw-gap-6" style={{ flexWrap: 'wrap' }}>
        {/* Card image preview sidebar (edit mode) */}
        {isEdit && imgSrc && (
          <div className="tw-flex-shrink-0" style={{ width: 160 }}>
            <div className="tw-rounded-xl tw-overflow-hidden tw-shadow-lg" style={{ border: '2px solid #e8e8e8' }}>
              <img src={imgSrc} alt="" className="tw-w-full tw-block" style={{ aspectRatio: '5/7', objectFit: 'cover' }} />
            </div>
            <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5">
              <button
                type="button"
                className="tw-w-full tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-flex tw-items-center tw-justify-center tw-gap-1.5"
                style={{ backgroundColor: '#3B82F6' }}
                disabled={self.state.uploadingImage}
                onClick={function() { self.handleUploadImage(self.state.editingId, 'image'); }}
              >
                {self.state.uploadingImage ? <i className="fa fa-spinner fa-spin" /> : <span><i className="fa fa-camera" /> Replace Image</span>}
              </button>
            </div>
          </div>
        )}

        {/* Form fields */}
        <div className="tw-flex-1" style={{ minWidth: 280 }}>
          {/* Identity Section */}
          <SectionHeader color="#3B82F6">Identity</SectionHeader>
          <div className="tw-rounded-xl tw-p-4 tw-mb-5" style={{ backgroundColor: 'rgba(59,130,246,0.04)', border: '1px solid rgba(59,130,246,0.1)' }}>
            <div className="tw-grid tw-gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div>
                <label className={labelCls}>Game</label>
                <select
                  className={selectCls}
                  value={self.state[p + 'Game']}
                  onChange={function(e) { var u = {}; u[p + 'Game'] = e.target.value; self.setState(u); }}
                >
                  <option value="">Select game...</option>
                  {self.state.availableGames.map(function(g) { return <option key={g._id} value={g._id}>{g.name}</option>; })}
                </select>
                <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-0.5 tw-mb-0">Filters available sets and rarities</p>
              </div>
              <div>
                <label className={labelCls}>Name <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" className={inputCls} value={self.state[p + 'Name']} onChange={function(e) { var u = {}; u[p + 'Name'] = e.target.value; self.setState(u); }} />
              </div>
              <div>
                <label className={labelCls}>Card Number</label>
                <input type="text" className={inputCls} placeholder="e.g. C001, A0055" value={self.state[p + 'CardNumber']} onChange={function(e) { var u = {}; u[p + 'CardNumber'] = e.target.value; self.setState(u); }} />
              </div>
              <div>
                <label className={labelCls}>Card Set</label>
                <DropdownWithCustom
                  value={self.state[cardSetKey]}
                  options={self.state[p + 'Game'] ? self.getGameSets(self.state[p + 'Game']) : self.state.availableSets}
                  onChange={function(v) { var u = {}; u[cardSetKey] = v; self.setState(u); }}
                  isCustom={self.state[customCardSetKey]}
                  onToggleCustom={function(v) { var u = {}; u[customCardSetKey] = v; self.setState(u); }}
                  placeholder="Select set..."
                />
                <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-0.5 tw-mb-0">From game's Sets &amp; Categories</p>
              </div>
              <div>
                <label className={labelCls}>Rarity <span style={{ color: '#EF4444' }}>*</span></label>
                <select
                  className={selectCls}
                  value={self.state[rarityKey]}
                  onChange={function(e) { var u = {}; u[rarityKey] = e.target.value; self.setState(u); }}
                >
                  <option value="">Select rarity...</option>
                  {(self.state[p + 'Game'] ? self.getGameRarities(self.state[p + 'Game']) : self.state.availableRarities).map(function(r) { return <option key={r} value={r}>{r}</option>; })}
                </select>
                <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-0.5 tw-mb-0">Controls badge color</p>
              </div>
              <div>
                <label className={labelCls}>Variant</label>
                <input type="text" className={inputCls} placeholder="e.g. Foil, Alt Art" value={self.state[p + 'Variant']} onChange={function(e) { var u = {}; u[p + 'Variant'] = e.target.value; self.setState(u); }} />
              </div>
              <div>
                <label className={labelCls}>Artist</label>
                <input type="text" className={inputCls} placeholder="e.g. John Smith" value={self.state[p + 'Artist']} onChange={function(e) { var u = {}; u[p + 'Artist'] = e.target.value; self.setState(u); }} />
              </div>
            </div>
          </div>

          {/* Game Mechanics Section — collapsible, fields driven by game's cardFields */}
          {hasAnyMechanics && <SectionHeader color="#8B5CF6" open={isEdit || self.state.sectionMechanics} onToggle={function() { self.setState(function(prev) { return { sectionMechanics: !prev.sectionMechanics }; }); }}>Game Mechanics</SectionHeader>}
          {hasAnyMechanics && (isEdit || self.state.sectionMechanics) && <div className="tw-rounded-xl tw-p-4 tw-mb-5" style={{ backgroundColor: 'rgba(139,92,246,0.04)', border: '1px solid rgba(139,92,246,0.1)' }}>
            <div className="tw-grid tw-gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
              {hasField('cardType') && <div>
                <label className={labelCls}>Card Type</label>
                <DropdownWithCustom
                  value={self.state[cardTypeKey]}
                  options={self.state.availableTypes}
                  onChange={function(v) { var u = {}; u[cardTypeKey] = v; self.setState(u); }}
                  isCustom={self.state[customCardTypeKey]}
                  onToggleCustom={function(v) { var u = {}; u[customCardTypeKey] = v; self.setState(u); }}
                  placeholder="Select type..."
                />
              </div>}
              {hasField('handCost') && <div>
                <label className={labelCls}>Hand Cost</label>
                <input type="text" className={inputCls} placeholder="e.g. 2" value={self.state[p + 'HandCost']} onChange={function(e) { var u = {}; u[p + 'HandCost'] = e.target.value; self.setState(u); }} />
              </div>}
              {hasField('turnCount') && <div>
                <label className={labelCls}>Turn Count</label>
                <input type="number" className={inputCls} placeholder="e.g. 3" value={self.state[p + 'TurnCount']} onChange={function(e) { var u = {}; u[p + 'TurnCount'] = e.target.value; self.setState(u); }} />
              </div>}
              {hasField('effectName') && <div>
                <label className={labelCls}>Effect Name</label>
                <input type="text" className={inputCls} placeholder="e.g. Flame Burst" value={self.state[p + 'EffectName']} onChange={function(e) { var u = {}; u[p + 'EffectName'] = e.target.value; self.setState(u); }} />
              </div>}
              {hasField('healthy') && <div>
                <label className={labelCls}>Healthy</label>
                <input type="text" className={inputCls} placeholder="e.g. 500" value={self.state[p + 'Healthy']} onChange={function(e) { var u = {}; u[p + 'Healthy'] = e.target.value; self.setState(u); }} />
              </div>}
              {hasField('injured') && <div>
                <label className={labelCls}>Injured</label>
                <input type="text" className={inputCls} placeholder="e.g. 300" value={self.state[p + 'Injured']} onChange={function(e) { var u = {}; u[p + 'Injured'] = e.target.value; self.setState(u); }} />
              </div>}
            </div>

            {/* Element */}
            {hasField('element') && <div className="tw-mt-4">
              <label className={labelCls}><i className="fa fa-tint tw-mr-1" style={{ color: '#8B5CF6', fontSize: 10 }} />Element</label>
              <CheckboxGroup
                options={self.state.availableElements}
                selected={self.state[p + 'Element']}
                onToggle={function(v) { self.toggleArrayValue(p + 'Element', v); }}
                newValue={self.state.newElementValue}
                onNewValueChange={function(v) { self.setState({ newElementValue: v }); }}
                onAddCustom={function() { self.addCustomArrayValue(p + 'Element', 'newElementValue'); }}
              />
            </div>}

            {/* Characteristic */}
            {hasField('characteristic') && <div className="tw-mt-4">
              <label className={labelCls}><i className="fa fa-tag tw-mr-1" style={{ color: '#8B5CF6', fontSize: 10 }} />Characteristic</label>
              <CheckboxGroup
                options={self.state.availableCharacteristics}
                selected={self.state[p + 'Characteristic']}
                onToggle={function(v) { self.toggleArrayValue(p + 'Characteristic', v); }}
                newValue={self.state.newCharacteristicValue}
                onNewValueChange={function(v) { self.setState({ newCharacteristicValue: v }); }}
                onAddCustom={function() { self.addCustomArrayValue(p + 'Characteristic', 'newCharacteristicValue'); }}
              />
              <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mt-3 tw-p-3 tw-rounded-lg" style={{ backgroundColor: 'rgba(139,92,246,0.06)', border: '1px dashed rgba(139,92,246,0.2)' }}>
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1" style={{ minWidth: 200 }}>
                  <span className="tw-text-xs tw-font-semibold tw-whitespace-nowrap" style={{ color: '#8B5CF6' }}>Name:</span>
                  <input
                    type="text"
                    className="tw-flex-1 tw-px-2.5 tw-py-1.5 tw-text-xs tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#111] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
                    placeholder="e.g. Richie, Krakaan..."
                    value={self.state[p + 'CharName']}
                    onChange={function(e) { var u = {}; u[p + 'CharName'] = e.target.value; self.setState(u); }}
                  />
                  {self.state[p + 'CharName'] && (
                    <button type="button" className="tw-text-[#999] tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs" onClick={function() { var u = {}; u[p + 'CharName'] = ''; self.setState(u); }}>
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1" style={{ minWidth: 220 }}>
                  <span className="tw-text-xs tw-font-semibold tw-whitespace-nowrap" style={{ color: '#8B5CF6' }}>Transformation:</span>
                  <input
                    type="text"
                    className="tw-flex-1 tw-px-2.5 tw-py-1.5 tw-text-xs tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#111] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
                    placeholder="e.g. Chrawnus, Sofie..."
                    value={self.state[p + 'CharTransformation']}
                    onChange={function(e) { var u = {}; u[p + 'CharTransformation'] = e.target.value; self.setState(u); }}
                  />
                  {self.state[p + 'CharTransformation'] && (
                    <button type="button" className="tw-text-[#999] tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs" onClick={function() { var u = {}; u[p + 'CharTransformation'] = ''; self.setState(u); }}>
                      <i className="fa fa-times" />
                    </button>
                  )}
                </div>
              </div>
            </div>}

            {/* Ability Cost */}
            {hasField('abilityCost') && <div className="tw-mt-4">
              <label className={labelCls}><i className="fa fa-bolt tw-mr-1" style={{ color: '#8B5CF6', fontSize: 10 }} />Ability Cost</label>
              <CheckboxGroup
                options={self.state.availableAbilityCosts}
                selected={self.state[p + 'AbilityCost']}
                onToggle={function(v) { self.toggleArrayValue(p + 'AbilityCost', v); }}
                newValue={self.state.newAbilityCostValue}
                onNewValueChange={function(v) { self.setState({ newAbilityCostValue: v }); }}
                onAddCustom={function() { self.addCustomArrayValue(p + 'AbilityCost', 'newAbilityCostValue'); }}
              />
            </div>}
          </div>}

          {/* Card Text Section — collapsible */}
          <SectionHeader color="#D4A843" open={isEdit || self.state.sectionText} onToggle={function() { self.setState(function(prev) { return { sectionText: !prev.sectionText }; }); }}>Card Text</SectionHeader>
          {(isEdit || self.state.sectionText) && <div className="tw-rounded-xl tw-p-4 tw-mb-5" style={{ backgroundColor: 'rgba(212,168,67,0.04)', border: '1px solid rgba(212,168,67,0.1)' }}>
            <div className="tw-grid tw-gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label className={labelCls}>Skill</label>
                <textarea className={inputCls + ' tw-resize-y'} rows="2" placeholder="Card's skill text" value={self.state[p + 'Skill']} onChange={function(e) { var u = {}; u[p + 'Skill'] = e.target.value; self.setState(u); }} />
              </div>
              <div>
                <label className={labelCls}>Card Text</label>
                <textarea className={inputCls + ' tw-resize-y'} rows="2" placeholder="Rules text or flavor text" value={self.state[p + 'Text']} onChange={function(e) { var u = {}; u[p + 'Text'] = e.target.value; self.setState(u); }} />
              </div>
            </div>
          </div>}

          {/* Media & Flags Section — collapsible */}
          <SectionHeader color="#10B981" open={isEdit || self.state.sectionMedia} onToggle={function() { self.setState(function(prev) { return { sectionMedia: !prev.sectionMedia }; }); }}>Media & Flags</SectionHeader>
          {(isEdit || self.state.sectionMedia) && <div className="tw-rounded-xl tw-p-4 tw-mb-2" style={{ backgroundColor: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.1)' }}>
            <div className="tw-grid tw-gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <label className={labelCls}>{isEdit ? 'Image URL' : 'Card Image'}</label>
                {!isEdit ? (
                  <div>
                    <input
                      ref={self.createImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={function(e) {
                        var file = e.target.files[0];
                        if (!file) return;
                        self.setState({ createImageFile: file, createImagePreview: URL.createObjectURL(file) });
                      }}
                    />
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <button
                        type="button"
                        className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-border tw-cursor-pointer hover:tw-opacity-80"
                        style={{ borderColor: '#d0d0d0', backgroundColor: 'transparent', color: '#666' }}
                        onClick={function() { self.createImageInputRef.current && self.createImageInputRef.current.click(); }}
                      >
                        <i className="fa fa-upload tw-mr-1" />Choose Image
                      </button>
                      {self.state.createImagePreview && (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <img src={self.state.createImagePreview} alt="Preview" className="tw-w-10 tw-h-14 tw-object-cover tw-rounded" />
                          <button
                            type="button"
                            className="tw-text-xs tw-text-[#EF4444] tw-bg-transparent tw-border-0 tw-cursor-pointer"
                            onClick={function() { self.setState({ createImageFile: null, createImagePreview: null }); }}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-1 tw-mb-0">JPEG, PNG, GIF, WebP — Max 5MB. Auto-compressed to JPEG.</p>
                  </div>
                ) : (
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {self.state[p + 'Image'] && (
                        <img src={self.state[p + 'Image'].indexOf('http') === 0 ? self.state[p + 'Image'] : constants.DOMAIN + '/' + self.state[p + 'Image']} alt="" className="tw-w-8 tw-h-8 tw-rounded tw-object-cover tw-flex-shrink-0" />
                      )}
                      <input type="text" className={inputCls} value={self.state[p + 'Image']} onChange={function(e) { var u = {}; u[p + 'Image'] = e.target.value; self.setState(u); }} />
                    </div>
                    <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-1 tw-mb-0">Use sidebar button to upload, or paste URL</p>
                  </div>
                )}
              </div>
              <div>
                <label className={labelCls}>{isEdit ? 'Back Image URL' : 'Back Image'}</label>
                {!isEdit ? (
                  <div>
                    <input
                      ref={self.createBackImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={function(e) {
                        var file = e.target.files[0];
                        if (!file) return;
                        self.setState({ createBackImageFile: file, createBackImagePreview: URL.createObjectURL(file) });
                      }}
                    />
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <button
                        type="button"
                        className="tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg tw-font-medium tw-border tw-cursor-pointer hover:tw-opacity-80"
                        style={{ borderColor: '#d0d0d0', backgroundColor: 'transparent', color: '#666' }}
                        onClick={function() { self.createBackImageInputRef.current && self.createBackImageInputRef.current.click(); }}
                      >
                        <i className="fa fa-upload tw-mr-1" />Choose Back Image
                      </button>
                      {self.state.createBackImagePreview && (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <img src={self.state.createBackImagePreview} alt="Preview" className="tw-w-10 tw-h-14 tw-object-cover tw-rounded" />
                          <button
                            type="button"
                            className="tw-text-xs tw-text-[#EF4444] tw-bg-transparent tw-border-0 tw-cursor-pointer"
                            onClick={function() { self.setState({ createBackImageFile: null, createBackImagePreview: null }); }}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-1 tw-mb-0">Optional — JPEG, PNG, GIF, WebP</p>
                  </div>
                ) : (
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {self.state[p + 'BackImage'] && (
                        <img src={self.state[p + 'BackImage'].indexOf('http') === 0 ? self.state[p + 'BackImage'] : constants.DOMAIN + '/' + self.state[p + 'BackImage']} alt="" className="tw-w-8 tw-h-8 tw-rounded tw-object-cover tw-flex-shrink-0" />
                      )}
                      <input type="text" className={inputCls} value={self.state[p + 'BackImage']} onChange={function(e) { var u = {}; u[p + 'BackImage'] = e.target.value; self.setState(u); }} />
                      {self.state.editingId && (
                        <button
                          type="button"
                          className="tw-text-xs tw-px-2 tw-py-1.5 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-flex-shrink-0"
                          style={{ backgroundColor: '#3B82F6' }}
                          disabled={self.state.uploadingBackImage}
                          onClick={function() { self.handleUploadImage(self.state.editingId, 'backImage'); }}
                        >
                          {self.state.uploadingBackImage ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-camera" />}
                        </button>
                      )}
                    </div>
                    <p className="tw-text-[10px] tw-text-[#aaa] tw-mt-1 tw-mb-0">Optional — paste URL or use upload button</p>
                  </div>
                )}
              </div>
            </div>
            <div className="tw-flex tw-gap-8 tw-mt-4 tw-pt-4" style={{ borderTop: '1px solid rgba(16,185,129,0.15)' }}>
              <ToggleSwitch
                checked={self.state[p + 'Playable']}
                onChange={function(v) { var u = {}; u[p + 'Playable'] = v; self.setState(u); }}
                label="Playable"
                color="#10B981"
              />
              <ToggleSwitch
                checked={self.state[p + 'Errata']}
                onChange={function(v) { var u = {}; u[p + 'Errata'] = v; self.setState(u); }}
                label="Has Errata"
                color="#f59e0b"
              />
              <ToggleSwitch
                checked={self.state[p + 'Horizontal']}
                onChange={function(v) { var u = {}; u[p + 'Horizontal'] = v; if (v && self.state[p + 'Rotation'] == null) { u[p + 'Rotation'] = -90; } self.setState(u); }}
                label="Horizontal"
                color="#8B5CF6"
              />
            </div>
            {/* Rotation — only shown when horizontal is on */}
            {self.state[p + 'Horizontal'] && (
            <div className="tw-mt-3">
              <label className="tw-text-xs tw-font-semibold tw-text-[#666] dark:tw-text-[#999] tw-mb-1 tw-block">Image Rotation</label>
              <select
                value={self.state[p + 'Rotation'] || 0}
                onChange={function(e) { var u = {}; u[p + 'Rotation'] = parseInt(e.target.value); self.setState(u); }}
                className="tw-w-full tw-bg-white dark:tw-bg-[#1a1a1a] tw-border tw-border-solid tw-border-[#ddd] dark:tw-border-[#444] tw-rounded tw-px-2 tw-py-1.5 tw-text-sm tw-text-[#333] dark:tw-text-white"
              >
                <option value={-90}>Left (-90°)</option>
                <option value={90}>Right (90°)</option>
              </select>
            </div>
            )}
          </div>}
        </div>
      </div>
    );
  }

  render() {
    var self = this;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var cards = this.state.cards;
    var pagination = this.state.pagination;
    var hasFilters = this.state.search || this.state.filterGame || this.state.filterSet || this.state.filterRarity || this.state.filterType || this.state.filterPlayable || this.state.filterErrata || this.state.filterArchived;

    return (
      <div>
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
          <div>
            <h2 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">Card Catalog</h2>
            <p className="tw-text-sm tw-text-[#999] tw-m-0 tw-mt-0.5">{pagination.total || 0} total cards</p>
          </div>
          <div className="tw-flex tw-gap-2">
            <button
              className="tw-text-xs tw-px-4 tw-py-2.5 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
              style={{ backgroundColor: '#3B82F6' }}
              onClick={this.handleSyncCards}
              disabled={this.state.syncing}
            >
              <i className={'fa ' + (this.state.syncing ? 'fa-spinner fa-spin' : 'fa-refresh') + ' tw-mr-1'} />
              {this.state.syncing ? 'Syncing...' : 'Sync Cards'}
            </button>
            <button
              className="tw-text-xs tw-px-4 tw-py-2.5 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
              style={{ backgroundColor: '#10B981' }}
              onClick={function() {
                var newMode = !self.state.createMode;
                var update = { createMode: newMode };
                if (newMode && self.state.filterGame) {
                  var matchGame = self.state.availableGames.find(function(g) { return g.slug === self.state.filterGame; });
                  if (matchGame) update.createGame = matchGame._id;
                }
                self.setState(update);
              }}
            >
              <i className={'fa ' + (self.state.createMode ? 'fa-times' : 'fa-plus') + ' tw-mr-1'} />
              {self.state.createMode ? 'Cancel' : 'Create Card'}
            </button>
            <button
              className="tw-text-xs tw-px-4 tw-py-2.5 tw-rounded-lg tw-font-medium tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90"
              style={{ backgroundColor: '#D4A843' }}
              onClick={this.handleExport}
            >
              <i className="fa fa-download tw-mr-1" />Export CSV
            </button>
          </div>
        </div>

        {/* Relationship explanation banner */}
        {!localStorage.getItem('admin_cards_banner_dismissed') && (
          <div className="tw-mb-4 tw-px-4 tw-py-3 tw-rounded-xl tw-flex tw-items-start tw-gap-3" style={{ backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.05)', border: '1px solid ' + (isDark ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.15)') }}>
            <div className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5" style={{ backgroundColor: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.12)' }}>
              <i className="fa fa-sitemap" style={{ color: '#8B5CF6', fontSize: '13px' }} />
            </div>
            <div className="tw-flex-1">
              <h4 className="tw-text-sm tw-font-bold tw-mb-1" style={{ color: isDark ? '#c4b5fd' : '#5b21b6' }}>How Cards Relate to Games</h4>
              <p className="tw-text-xs tw-mb-0 tw-leading-relaxed" style={{ color: isDark ? '#a78bfa' : '#7c3aed' }}>
                Cards belong to a <strong>Game</strong>, which defines available <strong>Sets</strong>, <strong>Rarities</strong>, and <strong>Card Attributes</strong>.
                Configure the game first in <strong>Games</strong> before creating cards here. Each card needs a game, set, and rarity at minimum.
              </p>
            </div>
            <button
              onClick={function() { localStorage.setItem('admin_cards_banner_dismissed', '1'); self.forceUpdate(); }}
              className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs tw-flex-shrink-0 tw-mt-1"
              style={{ color: isDark ? '#a78bfa' : '#7c3aed' }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        )}

        {/* Sync Result Banner */}
        {this.state.syncResult && (
          <div className="tw-mb-4 tw-px-4 tw-py-3 tw-rounded-lg tw-flex tw-items-start tw-gap-3" style={{
            backgroundColor: this.state.syncResult.error ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
            border: '1px solid ' + (this.state.syncResult.error ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'),
          }}>
            <i className={'fa ' + (this.state.syncResult.error ? 'fa-exclamation-circle' : 'fa-check-circle') + ' tw-mt-0.5'}
              style={{ color: this.state.syncResult.error ? '#EF4444' : '#10B981', fontSize: 16 }} />
            <div className="tw-flex-1">
              {this.state.syncResult.error ? (
                <span className="tw-text-sm" style={{ color: '#EF4444' }}>{this.state.syncResult.error}</span>
              ) : (
                <div>
                  <span className="tw-text-sm tw-font-semibold" style={{ color: '#10B981' }}>Sync completed successfully</span>
                  <div className="tw-text-xs tw-text-[#999] tw-mt-1">
                    {this.state.syncResult.inserted} new cards added &middot; {this.state.syncResult.updated} updated &middot; {this.state.syncResult.skippedFields} protected fields skipped
                    {this.state.syncResult.errors > 0 && <span className="tw-text-[#EF4444]"> &middot; {this.state.syncResult.errors} errors</span>}
                    &middot; {this.state.syncResult.totalLocal} total cards
                  </div>
                </div>
              )}
            </div>
            <button
              className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-[#999] hover:tw-text-[#666] tw-text-sm tw-p-0"
              onClick={function() { self.setState({ syncResult: null }); }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        )}

        {/* Create Card Panel */}
        {this.state.createMode && (
          <div className="tw-rounded-xl tw-border-2 tw-border-[#10B981] tw-mb-4 tw-overflow-hidden" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}>
            <div className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between" style={{ backgroundColor: '#10B981' }}>
              <h3 className="tw-text-sm tw-font-bold tw-text-white tw-m-0">
                <i className="fa fa-plus tw-mr-1.5" />New Card
              </h3>
              <button
                className="tw-text-white tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs hover:tw-opacity-80"
                onClick={function() { self.setState({ createMode: false }); }}
              >
                <i className="fa fa-chevron-up" />
              </button>
            </div>
            <div className="tw-p-5">
              {this.renderCardForm('create', null)}
              <div className="tw-flex tw-gap-3 tw-mt-5 tw-pt-4" style={{ borderTop: '1px solid rgba(16,185,129,0.2)' }}>
                <button
                  className="tw-text-sm tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-semibold tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-shadow-sm"
                  style={{ backgroundColor: '#10B981' }}
                  disabled={self.state.createLoading}
                  onClick={self.handleCreateCard}
                >
                  {self.state.createLoading ? <span><i className="fa fa-spinner fa-spin tw-mr-1" /> Creating...</span> : <span><i className="fa fa-plus tw-mr-1.5" />Create Card</span>}
                </button>
                <button
                  className="tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-font-medium tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer hover:tw-bg-[#f5f5f5]"
                  onClick={function() { self.setState({ createMode: false }); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="tw-rounded-xl tw-p-4 tw-mb-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
          <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-center">
            <div className="tw-relative tw-flex-1" style={{ minWidth: 200 }}>
              <i className="fa fa-search tw-absolute tw-left-3 tw-text-[#999]" style={{ top: '50%', transform: 'translateY(-50%)', fontSize: 12 }} />
              <input
                type="text"
                className="tw-w-full tw-pl-8 tw-pr-3 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
                placeholder="Search by name or card number..."
                value={this.state.search}
                onChange={this.handleSearchChange}
              />
            </div>
            {this.state.availableGames.length > 1 && (
              <select
                className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
                value={this.state.filterGame}
                onChange={function(e) { self.setState({ filterGame: e.target.value }, function() { self.fetchCards(1); }); }}
              >
                <option value="">All Games</option>
                {this.state.availableGames.map(function(g) { return <option key={g.slug} value={g.slug}>{g.name}</option>; })}
              </select>
            )}
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterSet}
              onChange={function(e) { self.setState({ filterSet: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">All Sets</option>
              {this.state.availableSets.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterRarity}
              onChange={function(e) { self.setState({ filterRarity: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">All Rarities</option>
              {this.state.availableRarities.map(function(r) { return <option key={r} value={r}>{r}</option>; })}
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterType}
              onChange={function(e) { self.setState({ filterType: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">All Types</option>
              {this.state.availableTypes.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterPlayable}
              onChange={function(e) { self.setState({ filterPlayable: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">Playable: All</option>
              <option value="true">Playable</option>
              <option value="false">Not Playable</option>
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterErrata}
              onChange={function(e) { self.setState({ filterErrata: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">Errata: All</option>
              <option value="true">Has Errata</option>
              <option value="false">No Errata</option>
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.filterArchived}
              onChange={function(e) { self.setState({ filterArchived: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">Archive: All</option>
              <option value="false">Active Only</option>
              <option value="true">Archived Only</option>
            </select>
            <select
              className="tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-sm tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-[#D4A843]"
              value={this.state.sortBy}
              onChange={function(e) { self.setState({ sortBy: e.target.value }, function() { self.fetchCards(1); }); }}
            >
              <option value="">Sort: A–Z</option>
              <option value="name_desc">Sort: Z–A</option>
              <option value="listings">Most Listings</option>
              <option value="price">Lowest Price</option>
              <option value="newest">Newest</option>
            </select>
            {hasFilters && (
              <button
                className="tw-text-xs tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer hover:tw-bg-[#f5f5f5]"
                onClick={function() { self.setState({ search: '', filterGame: '', filterSet: '', filterRarity: '', filterType: '', filterPlayable: '', filterErrata: '', filterArchived: '', sortBy: '' }, function() { self.fetchCards(1); }); }}
              >
                <i className="fa fa-times tw-mr-1" />Clear
              </button>
            )}
          </div>
        </div>

        {/* Loading */}
        {this.state.loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
            <div className="tw-text-center">
              <i className="fa fa-circle-o-notch fa-spin tw-text-3xl tw-mb-3" style={{ color: '#D4A843', display: 'block' }} />
              <span className="tw-text-sm tw-text-[#999]">Loading cards...</span>
            </div>
          </div>
        )}

        {/* Table */}
        {!this.state.loading && (
          <div className="tw-rounded-xl tw-overflow-hidden" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-text-sm" style={{ minWidth: '700px' }}>
                <thead>
                  <tr style={{ backgroundColor: isDark ? '#111' : '#fafafa', borderBottom: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Card</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden md:tw-table-cell">Number</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden md:tw-table-cell">Set</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Rarity</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden lg:tw-table-cell">Type</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden lg:tw-table-cell">Flags</th>
                    <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider tw-hidden md:tw-table-cell">Listings</th>
                    <th className="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-semibold tw-text-[#888] tw-uppercase tw-tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map(function(card) {
                    var isExpanded = self.state.expandedId === card._id;
                    var isEditing = self.state.editingId === card._id;
                    var rColor = getRarityColor(card.rarity, null);
                    var isManual = !card.primalTcgId;
                    var hasOverrides = card.manualOverrides && card.manualOverrides.length > 0;

                    return (
                      <React.Fragment key={card._id}>
                        <tr className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222] hover:tw-bg-[#f9f9f9] dark:hover:tw-bg-[#151515] tw-transition-colors">
                          <td className="tw-px-4 tw-py-3.5">
                            <div className="tw-flex tw-items-center tw-gap-2.5">
                              {card.image ? (
                                <img src={card.image.indexOf('http') === 0 ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-10 tw-h-14 tw-rounded tw-object-cover tw-flex-shrink-0" />
                              ) : (
                                <div className="tw-w-10 tw-h-14 tw-rounded tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: '#f0f0f0' }}>
                                  <i className="fa fa-clone tw-text-xs tw-text-[#ccc]" />
                                </div>
                              )}
                              <div className="tw-min-w-0">
                                <div className="tw-flex tw-items-center tw-gap-1.5">
                                  <span className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-truncate" style={{ maxWidth: 180 }}>{card.name}</span>
                                  {card.archived && (
                                    <span className="tw-text-[9px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-semibold tw-text-white" style={{ backgroundColor: '#EF4444' }}>Archived</span>
                                  )}
                                  {isManual && (
                                    <span className="tw-text-[9px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-semibold tw-text-white" style={{ backgroundColor: '#8B5CF6' }}>Manual</span>
                                  )}
                                  {hasOverrides && (
                                    <i className="fa fa-shield" style={{ color: '#f59e0b', fontSize: 11 }} title={'Sync-protected: ' + card.manualOverrides.join(', ')} />
                                  )}
                                </div>
                                {card.variant && <div className="tw-text-[10px] tw-text-[#999]">{card.variant}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-hidden md:tw-table-cell">{card.cardNumber || '—'}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-hidden md:tw-table-cell">{card.cardSet || '—'}</td>
                          <td className="tw-px-4 tw-py-3.5">
                            <span className="tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium tw-text-white" style={{ backgroundColor: rColor }}>{card.rarity || '—'}</span>
                          </td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-hidden lg:tw-table-cell">{card.cardType || '—'}</td>
                          <td className="tw-px-4 tw-py-3.5 tw-hidden lg:tw-table-cell">
                            <div className="tw-flex tw-gap-1">
                              {card.playable && (
                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-text-white" style={{ backgroundColor: '#10B981' }}>Play</span>
                              )}
                              {card.errata && (
                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-text-white" style={{ backgroundColor: '#f59e0b' }}>Errata</span>
                              )}
                              {card.horizontal && (
                                <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-text-white" style={{ backgroundColor: '#8B5CF6' }}>Horiz</span>
                              )}
                            </div>
                          </td>
                          <td className="tw-px-4 tw-py-3.5 tw-hidden md:tw-table-cell">
                            <span className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa]">{card.listingCount || 0}</span>
                            {card.lowestPrice != null && card.lowestPrice > 0 && (
                              <div className="tw-text-[10px]" style={{ color: '#D4A843' }}>${card.lowestPrice.toFixed(2)}</div>
                            )}
                          </td>
                          <td className="tw-px-4 tw-py-3.5 tw-text-right">
                            <button
                              className="tw-w-7 tw-h-7 tw-rounded tw-flex tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-[#999] hover:tw-text-[#3B82F6]"
                              title={isExpanded ? 'Collapse' : 'Expand'}
                              onClick={function() {
                                self.setState({
                                  expandedId: isExpanded ? null : card._id,
                                  editingId: null,
                                });
                              }}
                            >
                              <i className={'fa ' + (isExpanded ? 'fa-chevron-up' : 'fa-chevron-down')} style={{ fontSize: 11 }} />
                            </button>
                          </td>
                        </tr>
                        {/* Expanded Detail / Edit Row */}
                        {isExpanded && (
                          <tr className="tw-border-b tw-border-[#f0f0f0] dark:tw-border-[#222]">
                            <td colSpan="8" className="tw-px-6 tw-py-5 tw-bg-[#fafafa] dark:tw-bg-[#151515]">
                              {/* Sync protection banner */}
                              {hasOverrides && !isEditing && (
                                <div className="tw-mb-3 tw-px-3 tw-py-2.5 tw-rounded-lg" style={{ backgroundColor: '#fef3c7', borderLeft: '3px solid #f59e0b' }}>
                                  <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                                    <i className="fa fa-shield" style={{ color: '#f59e0b' }} />
                                    <span className="tw-text-xs tw-font-medium" style={{ color: '#92400e' }}>Sync-protected fields:</span>
                                    {card.manualOverrides.map(function(f) {
                                      return (
                                        <span
                                          key={f}
                                          className="tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium tw-cursor-pointer hover:tw-opacity-70"
                                          style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                                          title="Click to remove sync protection for this field"
                                          onClick={function() { self.handleClearOverride(card._id, f); }}
                                        >
                                          {f} <i className="fa fa-times" style={{ fontSize: 8, marginLeft: 2 }} />
                                        </span>
                                      );
                                    })}
                                  </div>
                                  <div className="tw-text-[10px] tw-mt-1.5" style={{ color: '#92400e', opacity: 0.8 }}>
                                    <i className="fa fa-info-circle tw-mr-1" />
                                    These fields were manually edited and won't be overwritten by card sync. Click the <strong>X</strong> on a field to remove protection and let the next sync update it.
                                  </div>
                                </div>
                              )}
                              {!isEditing ? (
                                <div>
                                  <div className="tw-flex tw-gap-6" style={{ flexWrap: 'wrap' }}>
                                    {/* Card Image */}
                                    <div className="tw-flex-shrink-0" style={{ width: 180 }}>
                                      {card.image ? (
                                        <div className="tw-rounded-xl tw-overflow-hidden tw-shadow-lg" style={{ border: '2px solid rgba(150,150,150,0.25)' }}>
                                          <img src={card.image.indexOf('http') === 0 ? card.image : constants.DOMAIN + '/' + card.image} alt="" className="tw-w-full tw-block" style={{ aspectRatio: '5/7', objectFit: 'cover' }} />
                                        </div>
                                      ) : (
                                        <div className="tw-w-full tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-bg-[#f0f0f0] dark:tw-bg-[#222]" style={{ aspectRatio: '5/7', border: '2px dashed rgba(150,150,150,0.3)' }}>
                                          <div className="tw-text-center">
                                            <i className="fa fa-image tw-text-3xl tw-text-[#ccc] tw-block tw-mb-2" />
                                            <span className="tw-text-[10px] tw-text-[#bbb]">No Image</span>
                                          </div>
                                        </div>
                                      )}
                                      {card.backImage && (
                                        <div className="tw-mt-2 tw-text-center">
                                          <span className="tw-text-[10px] tw-text-[#999]">Has back image</span>
                                        </div>
                                      )}
                                    </div>

                                    {/* Card Details */}
                                    <div className="tw-flex-1" style={{ minWidth: 300 }}>
                                      {/* Header: Name + badges */}
                                      <div className="tw-mb-4">
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mb-1">
                                          <h3 className="tw-text-xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">{card.name}</h3>
                                          {isManual && (
                                            <span className="tw-text-[10px] tw-px-2 tw-py-0.5 tw-rounded-full tw-font-bold tw-text-white" style={{ backgroundColor: '#8B5CF6' }}>MANUAL</span>
                                          )}
                                        </div>
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-[#666]">
                                          {card.cardNumber && <span className="tw-font-mono">{card.cardNumber}</span>}
                                          {card.cardNumber && card.cardSet && <span style={{ color: '#ddd' }}>|</span>}
                                          {card.cardSet && <span>{card.cardSet}</span>}
                                        </div>
                                      </div>

                                      {/* Quick Stats Row */}
                                      <div className="tw-flex tw-rounded-xl tw-overflow-hidden tw-mb-4 tw-bg-white dark:tw-bg-[#222]" style={{ border: '1px solid rgba(150,150,150,0.2)' }}>
                                        <StatBox label="Rarity" value={
                                          <span className="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium tw-text-white" style={{ backgroundColor: rColor }}>{card.rarity || '—'}</span>
                                        } />
                                        <div style={{ width: 1, backgroundColor: 'rgba(150,150,150,0.2)' }} />
                                        <StatBox label="Type" value={card.cardType || '—'} />
                                        <div style={{ width: 1, backgroundColor: 'rgba(150,150,150,0.2)' }} />
                                        <StatBox label="Hand Cost" value={card.handCost || '—'} />
                                        <div style={{ width: 1, backgroundColor: 'rgba(150,150,150,0.2)' }} />
                                        <StatBox label="Turn" value={card.turnCount != null ? card.turnCount : '—'} />
                                        {(card.healthy || card.injured) && <div style={{ width: 1, backgroundColor: 'rgba(150,150,150,0.2)' }} />}
                                        {(card.healthy || card.injured) && (
                                          <StatBox label="HP" value={(card.healthy || '—') + ' / ' + (card.injured || '—')} />
                                        )}
                                      </div>

                                      {/* Element & Characteristic pills */}
                                      {Array.isArray(card.element) && card.element.length > 0 && (
                                        <div className="tw-mb-3">
                                          <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-[#999] tw-font-semibold tw-mb-1.5">Elements</div>
                                          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {card.element.map(function(el) {
                                              return <PillBadge key={el} color="#3B82F6" icon="fa-tint">{el}</PillBadge>;
                                            })}
                                          </div>
                                        </div>
                                      )}
                                      {Array.isArray(card.characteristic) && card.characteristic.length > 0 && (
                                        <div className="tw-mb-3">
                                          <div className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-[#999] tw-font-semibold tw-mb-1.5">Characteristics</div>
                                          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                                            {card.characteristic.map(function(ch) {
                                              return <PillBadge key={ch} color="#8B5CF6" icon="fa-tag">{ch}</PillBadge>;
                                            })}
                                          </div>
                                        </div>
                                      )}

                                      {/* Card Text & Skill */}
                                      {(card.text || card.skill) && (
                                        <div className="tw-rounded-xl tw-p-3.5 tw-mb-3" style={{ backgroundColor: 'rgba(212,168,67,0.08)', border: '1px solid rgba(212,168,67,0.2)' }}>
                                          {card.skill && (
                                            <div className={card.text ? 'tw-mb-3 tw-pb-3' : ''} style={card.text ? { borderBottom: '1px solid rgba(212,168,67,0.15)' } : {}}>
                                              <span className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-font-bold" style={{ color: '#D4A843' }}>Skill</span>
                                              <p className="tw-text-sm tw-text-[#1a1a1a] dark:tw-text-[#e0e0e0] tw-m-0 tw-mt-1 tw-font-medium">{card.skill}</p>
                                            </div>
                                          )}
                                          {card.text && (
                                            <div>
                                              <span className="tw-text-[10px] tw-uppercase tw-tracking-wider tw-font-bold" style={{ color: '#D4A843' }}>Card Text</span>
                                              <p className="tw-text-sm tw-text-[#1a1a1a] dark:tw-text-[#e0e0e0] tw-m-0 tw-mt-1" style={{ lineHeight: '1.6' }}>{card.text}</p>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Marketplace Stats & Meta */}
                                      <div className="tw-flex tw-gap-3 tw-flex-wrap tw-mb-3">
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)' }}>
                                          <i className="fa fa-shopping-bag" style={{ color: '#10B981' }} />
                                          <span className="tw-font-medium" style={{ color: '#10B981' }}>
                                            {card.listingCount || 0} listing{(card.listingCount || 0) !== 1 ? 's' : ''}
                                            {card.lowestPrice != null && card.lowestPrice > 0 ? ' from $' + card.lowestPrice.toFixed(2) : ''}
                                          </span>
                                        </div>
                                        {(card.buylistCount || 0) > 0 && (
                                          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)' }}>
                                            <i className="fa fa-bullhorn" style={{ color: '#3B82F6' }} />
                                            <span className="tw-font-medium" style={{ color: '#3B82F6' }}>
                                              {card.buylistCount} buylist want{card.buylistCount !== 1 ? 's' : ''}
                                            </span>
                                          </div>
                                        )}
                                        {card.variant && (
                                          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg" style={{ backgroundColor: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)' }}>
                                            <i className="fa fa-star" style={{ color: '#8B5CF6' }} />
                                            <span className="tw-font-medium" style={{ color: '#8B5CF6' }}>Variant: {card.variant}</span>
                                          </div>
                                        )}
                                        {card.artist && (
                                          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-px-3 tw-py-2 tw-rounded-lg" style={{ backgroundColor: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.25)' }}>
                                            <i className="fa fa-paint-brush" style={{ color: '#D4A843' }} />
                                            <span className="tw-font-medium" style={{ color: '#D4A843' }}>{card.artist}</span>
                                          </div>
                                        )}
                                      </div>

                                      {/* Flags */}
                                      <div className="tw-flex tw-items-center tw-gap-3">
                                        {card.playable && (
                                          <span className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-font-medium tw-text-white" style={{ backgroundColor: '#10B981' }}>
                                            <i className="fa fa-check tw-mr-1" style={{ fontSize: 9 }} />Playable
                                          </span>
                                        )}
                                        {card.errata && (
                                          <span className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-font-medium tw-text-white" style={{ backgroundColor: '#f59e0b' }}>
                                            <i className="fa fa-exclamation-triangle tw-mr-1" style={{ fontSize: 9 }} />Errata
                                          </span>
                                        )}
                                        {card.horizontal && (
                                          <span className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-font-medium tw-text-white" style={{ backgroundColor: '#8B5CF6' }}>
                                            <i className="fa fa-arrows-h tw-mr-1" style={{ fontSize: 9 }} />Horizontal
                                          </span>
                                        )}
                                        {!card.playable && (
                                          <span className="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded-full tw-font-medium" style={{ backgroundColor: 'rgba(156,163,175,0.15)', color: '#9ca3af' }}>
                                            Not Playable
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Edit + Archive buttons */}
                                  <div className="tw-mt-5 tw-pt-4 tw-flex tw-gap-3" style={{ borderTop: '1px solid rgba(150,150,150,0.2)' }}>
                                    <button
                                      className="tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-font-semibold tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-shadow-sm"
                                      style={{ backgroundColor: '#3B82F6' }}
                                      onClick={function() {
                                      var parsed = parseCharacteristics(card.characteristic);
                                      self.setState({
                                        editingId: card._id,
                                        editGame: card.game || '',
                                        editName: card.name || '',
                                        editCardNumber: card.cardNumber || '',
                                        editCardSet: card.cardSet || '',
                                        editRarity: card.rarity || '',
                                        editCardType: card.cardType || '',
                                        editElement: Array.isArray(card.element) ? card.element.slice() : [],
                                        editCharacteristic: parsed.tags,
                                        editCharName: parsed.charName,
                                        editCharTransformation: parsed.charTransformation,
                                        editHandCost: card.handCost || '',
                                        editTurnCount: card.turnCount != null ? String(card.turnCount) : '',
                                        editEffectName: card.effectName || '',
                                        editSkill: card.skill || '',
                                        editText: card.text || '',
                                        editHealthy: card.healthy || '',
                                        editInjured: card.injured || '',
                                        editAbilityCost: Array.isArray(card.abilityCost) ? card.abilityCost.slice() : [],
                                        editVariant: card.variant || '',
                                        editArtist: card.artist || '',
                                        editImage: card.image || '',
                                        editBackImage: card.backImage || '',
                                        editPlayable: card.playable !== false,
                                        editErrata: card.errata === true,
                                        editHorizontal: card.horizontal === true,
                                        editRotation: card.rotation || 0,
                                        editCustomCardSet: false,
                                        editCustomCardType: false,
                                      });
                                    }}
                                  >
                                    <i className="fa fa-pencil tw-mr-1.5" />Edit Card
                                  </button>
                                    <button
                                      className="tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-font-semibold tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-shadow-sm"
                                      style={{ backgroundColor: card.archived ? '#10B981' : '#EF4444', color: '#fff' }}
                                      onClick={function() { self.handleToggleArchive(card._id, card.archived); }}
                                    >
                                      <i className={'fa ' + (card.archived ? 'fa-undo' : 'fa-archive') + ' tw-mr-1.5'} />{card.archived ? 'Unarchive' : 'Archive'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  {/* Edit mode header */}
                                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-pb-3" style={{ borderBottom: '2px solid #3B82F6' }}>
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                      <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: '#3B82F6' }}>
                                        <i className="fa fa-pencil tw-text-white" style={{ fontSize: 12 }} />
                                      </div>
                                      <div>
                                        <h4 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">Editing {card.name}</h4>
                                        <span className="tw-text-[10px] tw-text-[#999]">{card.cardNumber || ''} {card.cardSet ? '- ' + card.cardSet : ''}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Sync protection banner in edit mode */}
                                  {hasOverrides && (
                                    <div className="tw-mb-4 tw-px-4 tw-py-3 tw-rounded-xl" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                                      <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
                                        <i className="fa fa-shield" style={{ color: '#f59e0b', fontSize: 14 }} />
                                        <span className="tw-text-xs tw-font-semibold" style={{ color: '#92400e' }}>Sync-protected fields:</span>
                                        {card.manualOverrides.map(function(f) {
                                          return (
                                            <span
                                              key={f}
                                              className="tw-text-[10px] tw-px-2.5 tw-py-1 tw-rounded-full tw-font-semibold tw-cursor-pointer hover:tw-opacity-70"
                                              style={{ backgroundColor: '#f59e0b', color: '#fff' }}
                                              title="Click to remove sync protection for this field"
                                              onClick={function() { self.handleClearOverride(card._id, f); }}
                                            >
                                              {f} <i className="fa fa-times" style={{ fontSize: 8, marginLeft: 3 }} />
                                            </span>
                                          );
                                        })}
                                      </div>
                                      <div className="tw-text-[10px] tw-mt-1.5" style={{ color: '#92400e', opacity: 0.8 }}>
                                        <i className="fa fa-info-circle tw-mr-1" />
                                        These fields were manually edited and won't be overwritten by card sync. Click the <strong>X</strong> on a field to remove protection and let the next sync update it.
                                      </div>
                                    </div>
                                  )}

                                  {self.renderCardForm('edit', card)}

                                  {/* Save / Cancel */}
                                  <div className="tw-flex tw-gap-3 tw-mt-5 tw-pt-4" style={{ borderTop: '1px solid #e8e8e8' }}>
                                    <button
                                      className="tw-text-sm tw-px-6 tw-py-2.5 tw-rounded-xl tw-font-semibold tw-text-white tw-border-0 tw-cursor-pointer hover:tw-opacity-90 tw-shadow-sm"
                                      style={{ backgroundColor: '#10B981' }}
                                      disabled={self.state.editLoading}
                                      onClick={self.handleSaveEdit}
                                    >
                                      {self.state.editLoading ? <span><i className="fa fa-spinner fa-spin tw-mr-1" /> Saving...</span> : <span><i className="fa fa-check tw-mr-1.5" />Save Changes</span>}
                                    </button>
                                    <button
                                      className="tw-text-sm tw-px-5 tw-py-2.5 tw-rounded-xl tw-font-medium tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer hover:tw-bg-[#f5f5f5]"
                                      onClick={function() { self.setState({ editingId: null }); }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {cards.length === 0 && (
                    <tr><td colSpan="8" className="tw-px-4 tw-py-12 tw-text-center tw-text-[#999]">
                      <i className="fa fa-clone tw-text-3xl tw-mb-2" style={{ display: 'block' }} />No cards found
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-3" style={{ borderTop: '1px solid ' + (isDark ? '#2a2a2a' : '#e8e8e8') }}>
                <span className="tw-text-xs tw-text-[#999]">
                  Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <div className="tw-flex tw-gap-1">
                  <button
                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer disabled:tw-opacity-40"
                    disabled={pagination.page <= 1}
                    onClick={function() { self.fetchCards(pagination.page - 1); }}
                  >Prev</button>
                  <button
                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-border tw-border-[#d0d0d0] dark:tw-border-[#555] tw-rounded-lg tw-bg-transparent tw-text-[#666] dark:tw-text-[#aaa] tw-cursor-pointer disabled:tw-opacity-40"
                    disabled={pagination.page >= pagination.pages}
                    onClick={function() { self.fetchCards(pagination.page + 1); }}
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload error modal */}
        {this.state.uploadError && (
          <div
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={function() { self.setState({ uploadError: null }); }}
          >
            <div
              className="tw-rounded-xl tw-shadow-2xl tw-p-6 tw-max-w-md tw-mx-4"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: '1px solid ' + (isDark ? '#333' : '#e0e0e0') }}
              onClick={function(e) { e.stopPropagation(); }}
            >
              <div className="tw-flex tw-items-start tw-gap-3 tw-mb-4">
                <div className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#fef2f2' }}>
                  <i className="fa fa-exclamation-triangle" style={{ color: '#ef4444', fontSize: '18px' }} />
                </div>
                <div>
                  <h3 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1">Upload Failed</h3>
                  <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-0">{this.state.uploadError}</p>
                </div>
              </div>
              <div className="tw-flex tw-justify-end">
                <button
                  onClick={function() { self.setState({ uploadError: null }); }}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-colors"
                  style={{ backgroundColor: '#ef4444', color: '#fff' }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminCards;
